import cn from 'classnames';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useQueryFetch } from 'sonata-react-components';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import { getUsers } from '@/services/common.service';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { DeviatedLineItemsList } from '../DeviatedLineItemsList';
import {
  DEVIATED_LINE_ITEMS_COLUMN_VISIBILITY_KEY,
  DEVIATED_LINE_ITEMS_HIDEABLE_COLUMN_IDS,
  useDeviatedLineItemsColumnLabels
} from '../DeviatedLineItemsList/deviatedLineItemsColumns';
import EntityTabEmptyState from '../EntityTabEmptyState/EntityTabEmptyState';
import { ListFilters } from '../ListFilters';
import { AccountSelector } from '../ListFilters/AccountSelector';
import { DateRange } from '../ListFilters/DateRange';
import { DeviationRange } from '../ListFilters/DeviationRange';
import { SearchByFilters } from '../ListFilters/SearchByFilters';
import { LINE_ITEM_FILTER_GROUPS } from '../ListFilters/SearchByFilters/SearchByFilters.tsx';
import { StatusSelector } from '../ListFilters/StatusSelector';
import { getColumnVisibilityOptions } from '../Shared/columnVisibilityOptions';
import { buildDeviatedLineItemsRequestBody } from './deviatedLineItemsTab.helpers';

import type { SearchByFilterEntry } from '../ListFilters/SearchByFilters';

import './deviatedLineItemsTab.scss';

const EXCLUDED_ACCOUNT_USER_TYPES = ['inactive', 'internal-taptap'];

export default function DeviatedLineItemsTab() {
  const { applyVisibleColumns, isVisible: isColumnVisible } = useColumnVisibility(
    DEVIATED_LINE_ITEMS_COLUMN_VISIBILITY_KEY,
    DEVIATED_LINE_ITEMS_HIDEABLE_COLUMN_IDS
  );
  const columnLabels = useDeviatedLineItemsColumnLabels();
  const {
    cacheSeed,
    deviatedLineItemsListFilters,
    setCacheSeed,
    setDeviatedLineItemsListFilters,
    setShowSearchByFilters,
    showSearchByFilters
  } = useCampaignStore() as CampaignsStore;
  const { formatMessage } = useIntl();
  const [, setSearchParams] = useSearchParams();
  const [totalItems, setTotalItems] = useState<number | null>(null);

  const { data: allowedUsers } = useQueryFetch(['deviatedLineItems', 'allowedUsers'], () => getUsers());
  const accounts = (allowedUsers ?? [])
    .filter(
      (u: Record<string, any>) =>
        !EXCLUDED_ACCOUNT_USER_TYPES.includes(u.userType) && u.active === true && u.confirmed === true
    )
    .map(({ email, id, name }: Record<string, any>) => ({ _id: id, name: name?.trim() || email }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const minSelectableDate = moment().subtract(1, 'month').startOf('month');
  const maxSelectableDate = moment().endOf('month');

  const deviationFilter = deviatedLineItemsListFilters.advanceFilters?.find(
    (el: Record<string, any>) => el.id === 'deviation'
  );
  const accountsFilter = deviatedLineItemsListFilters.advanceFilters?.find(
    (el: Record<string, any>) => el.id === 'accounts'
  );
  const statusFilter = deviatedLineItemsListFilters.advanceFilters?.find(
    (el: Record<string, any>) => el.id === 'status'
  );
  const datesFilter = deviatedLineItemsListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'dates');

  const getFiltersByOrigin = (origin: 'advance' | 'searchBy'): SearchByFilterEntry[] => {
    const advanceFilters = deviatedLineItemsListFilters.advanceFilters || [];
    if (origin === 'advance') {
      return advanceFilters.filter((f: Record<string, any>) => !LINE_ITEM_FILTER_GROUPS.includes(f.id));
    }
    return advanceFilters.filter((f: Record<string, any>) => LINE_ITEM_FILTER_GROUPS.includes(f.id));
  };

  // No setCacheSeed() here: internalCacheSeed already includes JSON.stringify(filters), so changing
  // the filters is itself what invalidates the list's cache seed. A second store write would just
  // cause a second render and a duplicate fetch (Zustand doesn't batch it with the first).
  const handleFilter = (newFilters: Record<string, any>, isSearchByFiltersChange = false) => {
    if (!isSearchByFiltersChange) {
      setDeviatedLineItemsListFilters({
        ...newFilters,
        advanceFilters: [...newFilters.advanceFilters, ...getFiltersByOrigin('searchBy')]
      });
    } else {
      setDeviatedLineItemsListFilters({
        ...deviatedLineItemsListFilters,
        advanceFilters: [...getFiltersByOrigin('advance'), ...newFilters.advanceFilters]
      });
    }
  };

  const handleSearchByFiltersChange = (newFilters: SearchByFilterEntry[]) => {
    handleFilter({ ...deviatedLineItemsListFilters, advanceFilters: newFilters }, true);
  };

  const handleReset = () => {
    setDeviatedLineItemsListFilters({});
  };

  const leftFilters = [
    {
      className: 'campaignsListsFilters__leftFiltersSelector',
      component: <DeviationRange id="deviatedLineItemTabDeviationFilter" value={deviationFilter?.value} />,
      id: 'deviatedLineItemsFilters__deviationFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersSelector',
      component: (
        <AccountSelector accounts={accounts} id="deviatedLineItemTabAccountFilter" value={accountsFilter?.value} />
      ),
      id: 'deviatedLineItemsFilters__accountsFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersSelector',
      component: <StatusSelector id="deviatedLineItemTabStatusFilter" value={statusFilter?.value} />,
      id: 'deviatedLineItemsFilters__statusFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersDateRange',
      component: (
        <DateRange
          id="deviatedLineItemTab_date_filter"
          isOutsideRange={day => !day.isBetween(minSelectableDate, maxSelectableDate, 'day', '[]')}
          value={datesFilter?.value}
        />
      ),
      id: 'deviatedLineItemsFilters__dateRangeFilter',
      sendCallback: true
    }
  ];

  const requestBody = useMemo(
    () =>
      buildDeviatedLineItemsRequestBody(deviatedLineItemsListFilters.advanceFilters, deviatedLineItemsListFilters.text),
    [deviatedLineItemsListFilters.advanceFilters, deviatedLineItemsListFilters.text]
  );

  const hasActiveFilters =
    Boolean(deviatedLineItemsListFilters.text) || Boolean(deviatedLineItemsListFilters.advanceFilters?.length);
  const isEmpty = totalItems === 0 && !hasActiveFilters;

  if (isEmpty) {
    return (
      <EntityTabEmptyState
        buttonProps={{
          icon: 'fal fa-arrow-right',
          onClick: () =>
            setSearchParams(prevParams => {
              prevParams.set('tab', 'lineItem');
              return prevParams;
            }),
          text: formatMessage({
            defaultMessage: 'View Line Items',
            id: 'deviatedLineItems.emptyState.viewLineItemsButton'
          })
        }}
        description={formatMessage({
          defaultMessage: 'Every line item is delivering as expected. Check your Line Items tab for details.',
          id: 'deviatedLineItems.emptyState.description'
        })}
        title={formatMessage({
          defaultMessage: 'All line items on track',
          id: 'deviatedLineItems.emptyState.title'
        })}
      />
    );
  }

  return (
    <div
      className={cn('deviatedLineItemsTabContent', { 'deviatedLineItemsTabContent--withFilters': showSearchByFilters })}
    >
      <div className="deviatedLineItemsTabContent__main">
        <ListFilters
          extraActions={
            <ColumnVisibility
              columns={getColumnVisibilityOptions(
                columnLabels,
                DEVIATED_LINE_ITEMS_HIDEABLE_COLUMN_IDS,
                isColumnVisible
              )}
              onApply={applyVisibleColumns}
              prefixId="deviatedLineItemTab"
            />
          }
          initialFilters={deviatedLineItemsListFilters}
          leftFilters={leftFilters}
          onFilter={handleFilter}
          onReset={handleReset}
          onShowFiltersClick={setShowSearchByFilters}
          prefixId="deviatedLineItemTab"
          searchByFilters={getFiltersByOrigin('searchBy')}
        />
        <DeviatedLineItemsList
          cacheSeed={cacheSeed}
          isColumnVisible={isColumnVisible}
          onListChange={setCacheSeed}
          onTotalItemsChange={setTotalItems}
          requestBody={requestBody}
        />
      </div>
      {showSearchByFilters ? (
        <div className="deviatedLineItemsTabContent__filters">
          <SearchByFilters
            onClose={() => setShowSearchByFilters(false)}
            onFiltersChange={handleSearchByFiltersChange}
            value={getFiltersByOrigin('searchBy')}
            variant="lineItem"
          />
        </div>
      ) : null}
    </div>
  );
}
